import { clamp, isEqual } from 'lodash';
import { PluginKey } from '@tiptap/pm/state';
import { normalizeNonBreakingSpace } from '@/utils/helper-utils';
import { AllSelection, TextSelection, Transaction } from '@tiptap/pm/state';
import { type Command, type Editor, generateJSON, getSchema, isList, type JSONContent, type Range } from '@tiptap/react';
import {
  DOMParser as PMDOMParser,
  DOMSerializer,
  type Node as PmNode,
  type Schema,
  ResolvedPos,
  Slice,
  Fragment,
} from '@tiptap/pm/model';

import { baseExtensions } from '@/hooks/use-block-editor';

import { BlockNode, type BlockNodeStorage } from '@/extensions/block-node';
import { IndentConfig } from '@/extensions/indent/indent-config';

import { CustomDocument } from '@flintmint/core-shared/extensions';
import type { Block } from '@flintmint/core-shared/rxdb';
import type { TableOfContentData } from '@tiptap/extension-table-of-contents';

// tiptap `destroy()` nulls the editor `schema` and wipes `extensionStorage` but leaves the
// reference truthy in `editorAtom`, the session runtime, and the request-editor cache. Treat a
// destroyed editor as absent so reads like `getHTML`/`getAiToolkit` don't crash on the corpse.
export const isEditorAlive = (editor?: Editor | null): editor is Editor => !!editor && !editor.isDestroyed;

// `getAiToolkit` additionally throws when the AiToolkit extension is missing, so toolkit reads
// must confirm the extension storage is present, not just that the editor is alive.
export const isAiToolkitReady = (editor?: Editor | null): editor is Editor =>
  isEditorAlive(editor) && !!editor.storage?.aiToolkit;

export const getBlockNodeContent = (editor: Editor, node: PmNode) => {
  const serializer = DOMSerializer.fromSchema(editor.state.schema);
  const container = document.createElement('div');
  container.appendChild(serializer.serializeFragment(node.content));

  return {
    blockHtml: container.innerHTML,
    blockText: container.textContent ?? '',
  };
};

export interface BlockNodeWithPos {
  node: PmNode;
  startPos: number;
  endPos: number;
}

export const getBlockNodeByInstanceId = (editor: Editor, instanceId: string): BlockNodeWithPos | null => {
  let result: BlockNodeWithPos | null = null;

  editor.state.doc.descendants((node, pos) => {
    if (node.type.name === 'blockNode' && node.attrs.instanceId === instanceId) {
      result = { node, startPos: pos, endPos: pos + node?.nodeSize };
      return false;
    }
    return true;
  });

  return result;
};

export const getBlockStorage = (editor: Editor) => editor.storage.blockNode as BlockNodeStorage;

export const generateBlockNode = (block: Block, schema: Editor['schema'], instanceId: string) => {
  const jsonContent = generateJSON(block.content, baseExtensions);
  const contentNodes = jsonContent.content.map((child: JSONContent) => schema.nodeFromJSON(child));

  return schema.nodes.blockNode.create({ instanceId, id: block.id, name: block.name }, contentNodes);
};

export const isValidPosition = (doc: PmNode, pos: number) => {
  try {
    doc.resolve(pos);
    return true;
  } catch {
    return false;
  }
};

export const cleanHeadingAttributes = (html: string): string => {
  const container = document.createElement('div');
  container.innerHTML = html;

  container.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach((heading) => {
    heading.removeAttribute('id');
    heading.removeAttribute('data-toc-id');
  });

  return container.innerHTML;
};

export const isBlockNode = (node: PmNode) => {
  return node.type.name === 'blockNode';
};

export const findParentOfType = (
  $pos: ResolvedPos,
  typeName: string
): { node: PmNode; pos: number; attrs: Record<string, any> } | null => {
  for (let depth = $pos.depth; depth >= 0; depth--) {
    const node = $pos.node(depth);
    if (node.type.name === typeName) {
      return {
        node,
        attrs: node.attrs,
        pos: $pos.before(depth),
      };
    }
  }

  return null;
};

const parseHTML = (html: string): Document => {
  return new DOMParser().parseFromString(html, 'text/html');
};

const forEachBlockInstance = (doc: Document, fn: (el: Element) => void) => {
  doc.querySelectorAll('block-instance').forEach(fn);
};

export const normalizeBlockInstanceHTML = (html: string) => {
  const doc = parseHTML(html);

  forEachBlockInstance(doc, (block) => {
    const fragment = document.createDocumentFragment();
    while (block.firstChild) {
      fragment.appendChild(block.firstChild);
    }
    block.replaceWith(fragment);
  });

  return doc.body.innerHTML;
};

export const updateBlockInstanceIds = (html: string) => {
  const doc = parseHTML(html);

  forEachBlockInstance(doc, (block) => {
    block.setAttribute('data-block-instanceid', crypto.randomUUID());
  });

  return cleanHeadingAttributes(doc.body.innerHTML);
};

export const computeToCState = (scroller: HTMLElement, tocItems: TableOfContentData) => {
  const scrollBottom = scroller.scrollTop + scroller.clientHeight;
  const scrollHeight = scroller.scrollHeight;
  const isNearBottom = scrollBottom >= scrollHeight - 5 && scrollBottom !== scrollHeight;

  const enhancedItems = tocItems.map((item) => ({
    ...item,
    isActive: false,
  }));

  if (isNearBottom && enhancedItems.length) {
    enhancedItems[enhancedItems.length - 1].isActive = true;
    return enhancedItems;
  }

  const containerRect = scroller.getBoundingClientRect();

  let visibleMatch: { id: string; top: number } | null = null;
  let aboveViewportMatch: { id: string; top: number } | null = null;

  for (const item of enhancedItems) {
    const el = document.querySelector(`[data-toc-id="${item.id}"]`);
    if (!el) {
      continue;
    }

    const elRect = el.getBoundingClientRect();
    const distanceFromTop = elRect.top - containerRect.top;

    if (elRect.bottom > containerRect.top && elRect.top < containerRect.bottom) {
      // fully or partially visible
      if (!visibleMatch || distanceFromTop < visibleMatch.top) {
        visibleMatch = { id: item.id, top: distanceFromTop };
      }
    } else if (elRect.top < containerRect.top) {
      aboveViewportMatch = { id: item.id, top: distanceFromTop };
    }
  }

  const activeId = visibleMatch?.id ?? aboveViewportMatch?.id;

  if (activeId) {
    const index = enhancedItems.findIndex((item) => item.id === activeId);
    if (index !== -1) {
      enhancedItems[index].isActive = true;
    }
  }

  return enhancedItems;
};

export const extractBlockIds = (html: string) => {
  return Array.from(
    new Set(
      Array.from(new DOMParser().parseFromString(html, 'text/html').querySelectorAll('block-instance'))
        .map((el) => el.getAttribute('data-block-id'))
        .filter((id): id is string => !!id)
    )
  );
};

export const replaceSelectionWithSlice = (tr: Transaction, slice: Slice, collapseToEnd?: boolean): Transaction => {
  const { from, to } = tr.selection;

  tr.replaceRange(from, to, slice);

  const start = tr.mapping.map(from);
  const end = tr.mapping.map(to);

  const selectionStart = from === 1 || start === end ? from : start;

  if (collapseToEnd) {
    tr.setSelection(TextSelection.create(tr.doc, end));
  } else {
    tr.setSelection(TextSelection.create(tr.doc, selectionStart, end));
  }

  tr.scrollIntoView();

  return tr;
};

export const isWholeDocSelected = (editor: Editor) => {
  const { from, to } = editor.state.selection;
  return from === 1 && to >= editor.state.doc.nodeSize - 3;
};

export const isSelectionCrossesBlockBoundary = (editor: Editor) => {
  const { from, to } = editor.state.selection;

  const selectedAll = isWholeDocSelected(editor);

  if (selectedAll) {
    return false;
  }

  let foundBlock = false;
  let foundNonBlock = false;
  let blockCount = 0;

  if (from !== to) {
    editor.state.doc.nodesBetween(from, to, (node) => {
      if (node.type === editor.state.schema.nodes.blockNode) {
        blockCount += 1;
        foundBlock = true;
      } else {
        foundNonBlock = true;
      }
      return false;
    });
  }

  return (foundBlock && foundNonBlock) || (!selectedAll && blockCount >= 2);
};

export const computeNextHtml = (editor: Editor, range: Range, generated: string | Fragment): string => {
  const { state, schema } = editor;

  let tr = state.tr.setSelection(TextSelection.create(state.doc, range.from, range.to));

  let slice: Slice;

  if (typeof generated === 'string') {
    const wrapper = document.createElement('div');
    wrapper.innerHTML = generated.trim();
    slice = PMDOMParser.fromSchema(schema).parseSlice(wrapper);
  } else {
    slice = new Slice(generated, 0, 0);
  }

  const newState = state.apply(replaceSelectionWithSlice(tr, slice, true));

  const serializer = DOMSerializer.fromSchema(schema);
  const container = document.createElement('div');
  container.appendChild(serializer.serializeFragment(newState.doc.content));

  return container.innerHTML;
};

export const focusEditorStart = (editor: Editor) => {
  const { doc } = editor.state;
  let pos = 0;
  const firstNode = editor.state.doc.firstChild;

  if (firstNode?.type.name === 'horizontalRule') {
    doc.descendants((node, position) => {
      if (node.isTextblock && node.textContent.trim()) {
        pos = position + 1; // focus inside the text node
        return false; // stop descending
      }

      return true; // continue to next node
    });

    editor.commands.focus(pos, { scrollIntoView: true });
  } else {
    editor.commands.focus('start', { scrollIntoView: true });
  }
};

export const getActiveBlockId = (editor: Editor): string | undefined => {
  const { state } = editor;
  const { $from } = state.selection;

  for (let depth = $from.depth; depth >= 0; depth--) {
    const node = $from.node(depth);
    if (node.type === state.schema.nodes.blockNode) {
      return node.attrs.id;
    }
  }

  return undefined;
};

const setNodeIndentMarkup = (tr: Transaction, pos: number, delta: number): Transaction => {
  if (!tr.doc) return tr;

  const node = tr.doc.nodeAt(pos);
  if (!node) return tr;

  const minIndent = IndentConfig.min;
  const maxIndent = IndentConfig.max;

  const indent = clamp((node.attrs.indent || 0) + delta, minIndent, maxIndent);

  if (indent === node.attrs.indent) return tr;

  const nodeAttrs = {
    ...node.attrs,
    indent,
  };

  return tr.setNodeMarkup(pos, node.type, nodeAttrs, node.marks);
};

const updateIndentLevel = (tr: Transaction, delta: number, types: string[], editor: Editor): Transaction => {
  const { doc, selection } = tr;

  if (!doc || !selection) return tr;

  if (!(selection instanceof TextSelection || selection instanceof AllSelection)) {
    return tr;
  }

  const { from, to } = selection;

  doc.nodesBetween(from, to, (node, pos) => {
    const nodeType = node.type;

    if (types.includes(nodeType.name)) {
      tr = setNodeIndentMarkup(tr, pos, delta);
      return false;
    } else if (isList(node.type.name, editor.extensionManager.extensions)) {
      return false;
    }
    return true;
  });

  return tr;
};

export const createIndentCommand = ({ delta, types }: { delta: number; types: string[] }): Command => {
  return ({ state, dispatch, editor }) => {
    const { selection } = state;
    let { tr } = state;
    tr = tr.setSelection(selection);
    tr = updateIndentLevel(tr, delta, types, editor);

    if (tr.docChanged) {
      dispatch?.(tr);
      return true;
    }

    return false;
  };
};

/**
 * Checks if the current selection is in the first list item of its parent list
 */
export const isFirstListItem = (editor: Editor): boolean => {
  const { state } = editor;
  const { selection } = state;
  const { $from } = selection;

  let listItemNode: PmNode | null = null;
  let listNode: PmNode | null = null;

  for (let depth = $from.depth; depth >= 0; depth--) {
    const node = $from.node(depth);
    if (node.type.name === 'listItem') {
      listItemNode = node;
      // Find the parent list (bulletList or orderedList)
      for (let parentDepth = depth - 1; parentDepth >= 0; parentDepth--) {
        const parentNode = $from.node(parentDepth);
        if (isList(parentNode.type.name, editor.extensionManager.extensions)) {
          listNode = parentNode;
          break;
        }
      }
      break;
    }
  }

  if (!listItemNode || !listNode) {
    return false;
  }

  const firstChild = listNode.firstChild;
  return firstChild !== null && listItemNode === firstChild;
};

/**
 * Indents all list items in the current list by the specified delta
 */
export const indentEntireList = (editor: Editor, delta: number): boolean => {
  const { state } = editor;
  const { selection } = state;
  const { $from } = selection;

  // Find the parent list and its position
  let listPos = -1;
  let listNode: PmNode | null = null;

  for (let depth = $from.depth; depth >= 0; depth--) {
    const node = $from.node(depth);
    if (isList(node.type.name, editor.extensionManager.extensions)) {
      listPos = $from.before(depth);
      listNode = node;
      break;
    }
  }

  if (!listNode || listPos === -1) {
    return false;
  }

  const listItems: Array<{ node: PmNode; pos: number }> = [];

  let childPos = listPos + 1;
  listNode.forEach((node) => {
    if (node.type.name === 'listItem') {
      listItems.push({ node, pos: childPos });
    }
    childPos += node.nodeSize;
  });

  if (listItems.length) {
    // Apply indent to all list items
    let tr = state.tr;
    let changed = false;

    listItems.forEach(({ node, pos }) => {
      const currentIndent = node.attrs.indent || 0;
      const newIndent = Math.max(IndentConfig.min, Math.min(IndentConfig.max, currentIndent + delta));

      if (newIndent !== currentIndent) {
        const nodeAttrs = {
          ...node.attrs,
          indent: newIndent,
        };
        tr = tr.setNodeMarkup(pos, node.type, nodeAttrs, node.marks);
        changed = true;
      }
    });

    if (changed) {
      editor.view.dispatch(tr);
      return true;
    }
  }

  return false;
};

export const isBetweenText = (editor: Editor) => {
  const { state } = editor;
  const { selection } = state;

  if (selection.empty) {
    const { $from } = selection;

    const before = $from.nodeBefore;
    const after = $from.nodeAfter;

    return !!(before && after && before.isText && after.isText);
  }

  return false;
};

type ListItemContext = {
  node: PmNode;
  depth: number;
  pos: number;
};

export const getIndent = (node: PmNode): number => node.attrs.indent ?? 0;

export const findCurrentListItem = (editor: Editor): ListItemContext | null => {
  const { $from } = editor.state.selection;

  for (let depth = $from.depth; depth >= 0; depth--) {
    const node = $from.node(depth);
    if (node.type.name === 'listItem') {
      return {
        node,
        depth,
        pos: $from.before(depth),
      };
    }
  }

  return null;
};

const findParentList = (editor: Editor, listItemDepth: number): { node: PmNode; depth: number } | null => {
  const { $from } = editor.state.selection;

  for (let depth = listItemDepth - 1; depth >= 0; depth--) {
    const node = $from.node(depth);
    if (isList(node.type.name, editor.extensionManager.extensions)) {
      return { node, depth };
    }
  }

  return null;
};

const setListItemIndent = (editor: Editor, ctx: ListItemContext, indent: number): boolean => {
  const currentIndent = getIndent(ctx.node);
  if (currentIndent === indent) return false;

  const tr = editor.state.tr.setNodeMarkup(ctx.pos, ctx.node.type, { ...ctx.node.attrs, indent }, ctx.node.marks);

  editor.view.dispatch(tr);
  return true;
};

export const removeListItemIndent = (editor: Editor): boolean => {
  const ctx = findCurrentListItem(editor);
  if (!ctx) return false;

  return setListItemIndent(editor, ctx, 0);
};

export const restoreListItemIndent = (editor: Editor): boolean => {
  const { state } = editor;
  const { $from } = state.selection;

  const ctx = findCurrentListItem(editor);
  if (!ctx) return false;

  const parent = findParentList(editor, ctx.depth);
  if (!parent) return false;

  const { node: parentListNode, depth: parentListDepth } = parent;

  const listItemIndex = $from.index(parentListDepth);

  let previousIndent = 0;

  parentListNode.forEach((node, _offset, index) => {
    if (index >= listItemIndex) {
      return false;
    }

    if (node.type.name === 'listItem') {
      previousIndent = getIndent(node);
    }

    return true;
  });

  return setListItemIndent(editor, ctx, previousIndent);
};

export const getMarkerPos = (editor: Editor, key: PluginKey, markerId: string) => {
  const state = key.getState(editor.state);

  if (state) {
    const matches = state.decos.find(0, editor.state.doc.nodeSize, (spec: any) => spec?.key === markerId);
    return matches[0]?.from ?? null;
  }

  return null;
};

export function getBlockRangeBoundaries(editor: Editor, position?: { from: number; to: number }) {
  const initialPos = position ?? { from: 0, to: editor.state.doc.content.size || 0 };

  if (!position) {
    return {
      initialPos,
      safeFrom: initialPos.from,
      safeTo: initialPos.to,
    };
  }

  const $from = editor.state.doc.resolve(initialPos.from);
  const $to = editor.state.doc.resolve(initialPos.to);

  const br = $from.blockRange($to) ?? $from.blockRange($from);

  const safeFrom = br ? br.start : initialPos.from;
  const safeTo = br ? br.end : initialPos.to;

  return { initialPos, safeFrom, safeTo };
}

/**
 * Normalizes the selection range used for LLM operations.
 *
 * For most selections this returns the current selection range unchanged.
 * However, when the selection spans multiple list items within the same list,
 * ProseMirror's blockRange will usually have the parent list node (orderedList/bulletList)
 * as its parent. In that case, we expand the range to cover the entire list node.
 */
export const getNormalizedLlmSelectionRange = (editor: Editor): { from: number; to: number } => {
  const { doc } = editor.state;
  const { from, to } = editor.state.selection;

  const $from = doc.resolve(from);
  const $to = doc.resolve(to);

  const blockRange = $from.blockRange($to);

  if (blockRange && isList(blockRange.parent.type.name, editor.extensionManager.extensions)) {
    return {
      from: blockRange.start,
      to: blockRange.end,
    };
  }

  return { from, to };
};

const stripAttributes = (root: HTMLElement, attrs: string[]) => {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT);

  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const el = node as Element;

    attrs.forEach((attr) => {
      el.removeAttribute(attr);
    });
  }
};

let comparisonSchema: Schema | undefined;

const getComparisonSchema = () => {
  comparisonSchema ??= getSchema([BlockNode, CustomDocument, ...baseExtensions.filter((extension) => extension.name !== 'doc')]);
  return comparisonSchema;
};

const PARSE_CACHE_MAX_ENTRIES = 8;

let parseCacheBySchema = new WeakMap<Schema, Map<string, PmNode>>();

const parsePMHTML = (html: string, schema: Schema) => {
  let cache = parseCacheBySchema.get(schema);
  if (!cache) {
    cache = new Map();
    parseCacheBySchema.set(schema, cache);
  }

  const cached = cache.get(html);
  if (cached) {
    cache.delete(html);
    cache.set(html, cached);
    return cached;
  }

  const dom = new window.DOMParser().parseFromString(normalizeNonBreakingSpace(html), 'text/html');
  stripAttributes(dom.body, ['_hash']);
  const doc = PMDOMParser.fromSchema(schema).parse(dom.body);

  cache.set(html, doc);
  if (cache.size > PARSE_CACHE_MAX_ENTRIES) {
    cache.delete(cache.keys().next().value as string);
  }

  return doc;
};

let htmlByDoc = new WeakMap<PmNode, string>();

// Caches hold parsed/serialized document content (PHI); reset them when local data is wiped
// (logout) so note content does not outlive the session in a long-lived tab.
export const clearEditorContentCaches = () => {
  parseCacheBySchema = new WeakMap();
  htmlByDoc = new WeakMap();
};

// ProseMirror docs are immutable — every transaction produces a new `state.doc` instance,
// so the same doc reference always serializes to the same HTML. Callers must guard with
// `isEditorAlive` before calling: `destroy()` nulls `schema` but keeps `state`, so a
// destroyed editor's retained `state.doc` would make this memo silently return stale HTML.
export const getEditorHTML = (editor: Editor): string => {
  const doc = editor.state.doc;
  const cached = htmlByDoc.get(doc);
  if (cached !== undefined) return cached;
  const html = editor.getHTML();
  htmlByDoc.set(doc, html);
  return html;
};

export const isEqualHTML = (a: string, b: string, editor?: Editor | null): boolean => {
  if (a === b) return true;
  const schema = isEditorAlive(editor) ? editor.schema : getComparisonSchema();
  const docA = parsePMHTML(a, schema);
  const docB = parsePMHTML(b, schema);
  return docA.eq(docB);
};

export const isEqualWithHTML = (a: Record<string, any>, b: Record<string, any>): boolean => {
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length || !keysA.every((key) => Object.prototype.hasOwnProperty.call(b, key))) return false;

  return keysA.every((key) => {
    const valA = a[key];
    const valB = b[key];

    if (typeof valA === 'string' && typeof valB === 'string' && isHTMLString(valA)) {
      return isEqualHTML(valA, valB);
    }

    return isEqual(valA, valB);
  });
};

const isHTMLString = (str: string): boolean => /<[a-z][\s\S]*>/i.test(str);
